import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { CustomError, RequestMetadata, RequestOptions } from '../../../shared/models/api';
import { HANDLED_ERRORS } from '../../../shared/utils/http-context';

import { Observable, of, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private defaultRequestMetadata: RequestMetadata = {
    context: new HttpContext().set(HANDLED_ERRORS, []),
  };

  constructor(private httpClient: HttpClient) {}

  protected delete<T>({ errorResult, metadata = {}, operation, url }: Omit<RequestOptions, 'body'>): Observable<T> {
    return this.httpClient
      .delete<T>(url, {
        ...this.defaultRequestMetadata,
        ...metadata,
      })
      .pipe(
        tap(() => this.debugLog(operation, url, metadata)),
        catchError(this.handleError<typeof errorResult>(operation, errorResult))
      );
  }

  protected get<T>({ errorResult, metadata = {}, operation, url }: Omit<RequestOptions, 'body'>): Observable<T> {
    return this.httpClient
      .get<T>(url, {
        ...this.defaultRequestMetadata,
        ...metadata,
      })
      .pipe(
        tap(() => this.debugLog(operation, url, metadata)),
        catchError(this.handleError<typeof errorResult>(operation, errorResult))
      );
  }

  protected getHttpParams(params: Record<any, any>, isFiltered: boolean = true): { params: HttpParams } {
    if (!isFiltered) {
      return {
        params: new HttpParams({
          fromObject: params,
        }),
      };
    }

    // Filter out null/undefined parameters
    const filteredParams = Object.entries(params).reduce((filteredParamsObj, [paramKey, paramValue]) => {
      if (paramValue != null) {
        filteredParamsObj[paramKey] = paramValue;
      }

      return filteredParamsObj;
    }, {} as Record<string, any>);

    // Return encoded http params
    return {
      params: new HttpParams({
        fromObject: filteredParams,
      }),
    };
  }

  /**
   * Handle Http operation that failed.
   * Let the app continue.
   * @param operation - name of the operation that failed
   * @param result - optional value to return as the observable result
   */
  protected handleError<T>(operation = 'operation', result?: T) {
    return (error: CustomError): Observable<T> => {
      // Log message. Can use additional loggers such as ngxLogger.
      console.error(`${operation} failed: ${error.message}`);
      if (result != null) {
        return of(result as T);
      }

      // Let the app keep running by returning an empty result.
      if (result != null) {
        return of(result as T);
      }

      return throwError(error);
    };
  }

  protected patch<T>({ body = null, errorResult, metadata = {}, operation, url }: RequestOptions): Observable<T> {
    return this.httpClient
      .patch<T>(url, body, {
        ...this.defaultRequestMetadata,
        ...metadata,
      })
      .pipe(
        tap(() => this.debugLog(operation, url, metadata)),
        catchError(this.handleError<typeof errorResult>(operation, errorResult))
      );
  }

  protected post<T>({ body = null, errorResult, metadata = {}, operation, url }: RequestOptions): Observable<T> {
    return this.httpClient
      .post<T>(url, body, {
        ...this.defaultRequestMetadata,
        ...metadata,
      })
      .pipe(
        tap(() => this.debugLog(operation, url, metadata)),
        catchError(this.handleError<typeof errorResult>(operation, errorResult))
      );
  }

  protected put<T>({ body = null, errorResult, metadata = {}, operation, url }: RequestOptions): Observable<T> {
    return this.httpClient
      .put<T>(url, body, {
        ...this.defaultRequestMetadata,
        ...metadata,
      })
      .pipe(
        tap(() => this.debugLog(operation, url, metadata)),
        catchError(this.handleError<typeof errorResult>(operation, errorResult))
      );
  }

  /**
   * For debugging purposes.
   * @param operation - name of the operation that failed
   * @param url - optional value to return as the observable result
   * @param metadata - optional value to return as the observable result
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private debugLog(operation: string, url: string, metadata: RequestMetadata): void {
  }
}
